import { TextInput } from "react-admin";
import { FormProvider, useForm } from "react-hook-form";
import { DateTime } from "luxon";
import { DateTimeInput } from "../../inputs/DateTimeInput";
import { Column } from "../../misc/Column";
import { Row } from "../../misc/Row";
import { SelectInput } from "../../inputs/SelectInput";
import { DateInput } from "../../inputs/DateInput";
import { TimeInput } from "../../inputs/TimeInput";
import { Form2Theme } from "../../misc/Form2";
import { OtherEvent, OtherEventDuration } from "../../model/autovioCalendarEvents";
import { ReactElement, useMemo } from "react";
import { type SxProps } from "@mui/material";
import { SaveButton2 } from "../../buttons/SaveButton2";
import { otherEventDurationLabels } from "../../utils/calendar";
import { useOtherEventCategories } from "../../hooks/useOtherEventCategories";
import { isDefined } from "../../utils/filters";
import { VehicleInput } from "../../inputs/VehicleInput";

export interface OtherEventFormValues {
  categoryId: null | string;
  start: null | DateTime;
  endDate: null | DateTime;
  halfDayStartTime?: null | DateTime; // used only for vacation events with "halfDay" duration.
  end: null | DateTime;
  duration: null | OtherEventDuration;
  notes: null | string;
  vehicleId: null | string;
}

export function OtherEventForm(props: {
  initialDateTime?: DateTime;
  otherEvent?: OtherEvent;
  onSubmit: (formValues: OtherEventFormValues) => Promise<void>;
  sx?: SxProps;
  backButton?: ReactElement;
}) {
  const { initialDateTime, otherEvent, onSubmit, sx, backButton } = props;
  const otherEventCategories = useOtherEventCategories();
  const defaultValues = useMemo<OtherEventFormValues>(() => {
    const initialEventCategory = otherEventCategories.find((c) => c.id === otherEvent?.categoryId);
    const isInitialEventHalfDayVacation =
      initialEventCategory?.type === "vacation" &&
      ["halfDayInTheMorning", "halfDayInTheAfternoon"].includes(otherEvent?.otherEventDuration ?? "");

    return otherEvent
      ? {
          categoryId: otherEvent.categoryId,
          start: otherEvent.start,
          endDate: null,
          halfDayStartTime: isInitialEventHalfDayVacation ? otherEvent.start : null,
          end: otherEvent.otherEventDuration ? null : otherEvent.end,
          // vacation events replaces halfDayInTheMorning and halfDayInTheAfternoon with halfDay in the UI only.
          duration: otherEvent.otherEventDuration
            ? isInitialEventHalfDayVacation
              ? "halfDay"
              : otherEvent.otherEventDuration
            : null,
          vehicleId: otherEvent.vehicleId ?? null,
          notes: otherEvent.notes ?? "",
        }
      : {
          categoryId: null,
          start: initialDateTime ?? null,
          endDate: initialDateTime?.startOf("day") ?? null,
          end: null,
          duration: null,
          vehicleId: null,
          notes: null,
        };
  }, [otherEvent, initialDateTime, otherEventCategories]);
  const formProps = useForm<OtherEventFormValues>({ defaultValues });
  const [categoryId, start, duration] = formProps.watch(["categoryId", "start", "duration"]);
  const otherEventCategoryOptions = otherEventCategories
    .filter((c) => !c.deleted)
    .map((c) => {
      const categorySuffix = !isDefined(c.isPaid) ? "" : c.isPaid ? " (bezahlt)" : " (nicht bezahlt)";
      return [c.id, `${c.name}${categorySuffix}`] satisfies [string, string];
    })
    .sort(([, labelA], [, labelB]) => labelA.localeCompare(labelB, "de"));
  const selectedCategory = otherEventCategories.find((c) => c.id === categoryId);
  const isVacationEvent = selectedCategory?.type === "vacation";
  const supportsDateRange = !otherEvent && ["sickness", "vacation", "training"].includes(selectedCategory?.type ?? "");
  const otherEventDurationOptions = (
    isVacationEvent ? (["fullDay", "halfDay"] as const) : (selectedCategory?.availableDurations ?? [])
  ).map((d) => [d, otherEventDurationLabels[d]] satisfies [string, string]);
  return (
    <Form2Theme>
      <FormProvider {...formProps}>
        <form onSubmit={formProps.handleSubmit(onSubmit)} style={{ height: "100%" }}>
          <Column height="100%" minHeight={500} gap={1} sx={sx}>
            <SelectInput
              label="Kategorie"
              source="categoryId"
              options={otherEventCategoryOptions}
              validate={(value) => (value ? true : "Bitte wähle eine Kategorie aus.")}
              size="small"
              required
            />
            {selectedCategory && (
              <>
                {otherEventDurationOptions.length > 0 ? (
                  <>
                    <DateInput label={supportsDateRange ? "Von" : "Datum"} source="start" allowPastDates size="small" />
                    {supportsDateRange && (
                      <DateInput
                        label="Bis"
                        source="endDate"
                        allowPastDates
                        size="small"
                        validate={(endDate) => {
                          if (!endDate) {
                            return "Bitte wähle ein Enddatum aus.";
                          }
                          if (start?.isValid && endDate.startOf("day") < start.startOf("day")) {
                            return "Das Enddatum darf nicht vor dem Startdatum liegen.";
                          }
                          return true;
                        }}
                      />
                    )}
                    <SelectInput
                      label="Dauer"
                      source="duration"
                      options={otherEventDurationOptions}
                      size="small"
                      required
                      validate={(value) => (value ? true : "Bitte wähle die Dauer.")}
                    />
                    {isVacationEvent && duration === "halfDay" && (
                      <TimeInput source="halfDayStartTime" label="Zeit" size="small" />
                    )}
                  </>
                ) : (
                  <>
                    <DateTimeInput label="Start" source="start" allowPastDates size="small" />
                    <TimeInput
                      source="end"
                      label="Ende"
                      size="small"
                      validate={(end) => {
                        if (start?.isValid && end?.isValid) {
                          if (end.hour < start.hour || (end.hour === start.hour && end.minute <= start.minute)) {
                            return "Der Endzeitpunkt muss nach dem Startzeitpunkt liegen.";
                          }
                        }
                        return true;
                      }}
                    />
                  </>
                )}
              </>
            )}
            <VehicleInput source="vehicleId" label="Fahrzeug" size="small" />
            <TextInput label="Notizen" source="notes" rows={4} fullWidth multiline size="small" />
            <Row sx={{ justifyContent: "space-between", mt: "auto" }}>
              {backButton || <div />}
              <SaveButton2 />
            </Row>
          </Column>
        </form>
      </FormProvider>
    </Form2Theme>
  );
}
